
// Nickname generator for Hormony app
// Generates fun, hormone/wellness-themed nicknames and avatars

// Thematic adjectives related to hormonal wellness
const adjectives = [
  'Balanced', 'Radiant', 'Vibrant', 'Harmonious', 'Zen', 'Calm',
  'Peaceful', 'Energetic', 'Mindful', 'Glowing', 'Serene', 'Joyful',
  'Vital', 'Rhythmic', 'Empowered', 'Blooming', 'Thriving', 'Resilient',
  'Focused', 'Aligned', 'Natural', 'Holistic', 'Luminous', 'Restored', 
  'Revived', 'Dynamic', 'Refreshed', 'Nourished', 'Essential', 'Balanced',
  'Synced', 'Attuned', 'Aligned', 'Mindful', 'Centered', 'Grounded'
];

// Thematic nouns related to hormonal wellness
const nouns = [
  'Harmony', 'Wellness', 'Balance', 'Energy', 'Vitality', 'Flow',
  'Cycle', 'Bloom', 'Essence', 'Spirit', 'Glow', 'Aura', 'Moon',
  'Sun', 'Rhythm', 'Wave', 'Garden', 'Lotus', 'Stream', 'Meadow',
  'Breeze', 'Forest', 'Petal', 'Dawn', 'River', 'Ocean', 'Mountain',
  'Valley', 'Blossom', 'Butterfly', 'Sunflower', 'Phoenix', 'Dolphin',
  'Melody', 'Echo', 'Nectar', 'Compass', 'Journey', 'Path', 'Zenith'
];

// Nature-themed suffixes for diversity
const natureSuffixes = [
  'Bee', 'Bird', 'Dove', 'Swan', 'Seed', 'Leaf', 'Root', 'Wind',
  'Rain', 'Dew', 'Star', 'Sky', 'Cloud', 'Lake', 'Rose', 'Sage',
  'Oak', 'Pine', 'Mist', 'Ray', 'Drop', 'Tide', 'Echo', 'Fern'
];

// Thematic avatar base URLs
// In production, these would be actual image paths
const thematicAvatars = [
  '/avatars/harmony-1.png',
  '/avatars/harmony-2.png',
  '/avatars/harmony-3.png',
  '/avatars/harmony-4.png',
  '/avatars/harmony-5.png',
  '/avatars/harmony-6.png',
  '/avatars/harmony-7.png',
  '/avatars/harmony-8.png',
];

// For demo, we'll use placeholder avatars instead
const placeholderAvatars = [
  'https://randomuser.me/api/portraits/women/44.jpg',
  'https://randomuser.me/api/portraits/women/33.jpg',
  'https://randomuser.me/api/portraits/women/68.jpg',
  'https://randomuser.me/api/portraits/women/17.jpg',
  'https://randomuser.me/api/portraits/women/90.jpg',
  'https://randomuser.me/api/portraits/women/12.jpg',
  'https://randomuser.me/api/portraits/women/28.jpg',
  'https://randomuser.me/api/portraits/women/56.jpg',
];

/**
 * Generate a random nickname
 * @returns A hormone/wellness-themed nickname with a random number
 */
export const generateNickname = (): string => {
  const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
  const randomNumber = Math.floor(Math.random() * 1000);
  
  return `${randomAdjective}${randomNoun}${randomNumber}`;
};

/**
 * Generate a more diverse nickname with nature theme
 * @returns A hormone/wellness-themed nickname with a nature suffix
 */
export const generateThematicNickname = (): string => {
  const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const randomSuffix = natureSuffixes[Math.floor(Math.random() * natureSuffixes.length)];
  const randomNumber = Math.floor(Math.random() * 1000);
  
  return `${randomAdjective}${randomSuffix}${randomNumber}`;
};

/**
 * Generate a random avatar URL
 * @returns A URL to a thematic avatar image
 */
export const generateAvatar = (): string => {
  // In production, use the thematicAvatars array
  // For demo, we'll use placeholderAvatars
  return placeholderAvatars[Math.floor(Math.random() * placeholderAvatars.length)];
};

/**
 * Check if a nickname is already taken
 * In a real app, this would check against a database
 * @param nickname The nickname to check
 * @returns True if the nickname is available
 */
export const isNicknameAvailable = (nickname: string): boolean => {
  // In a real app, this would check against a database of existing nicknames
  // For now, just simulate a random availability check (90% chance of being available)
  return Math.random() > 0.1;
};

/**
 * Generate a unique nickname by ensuring it's not already taken
 * @returns A unique nickname
 */
export const generateUniqueNickname = (): string => {
  let nickname: string;
  let attempts = 0;
  
  // Try both standard and thematic nicknames
  const useThematic = Math.random() > 0.5;
  
  do {
    nickname = useThematic ? generateThematicNickname() : generateNickname();
    attempts++;
  } while (!isNicknameAvailable(nickname) && attempts < 10);
  
  return nickname;
};

/**
 * Generate both a unique nickname and avatar
 * @returns An object containing nickname and avatar URL
 */
export const generateUserIdentity = (): { nickname: string; avatar: string } => {
  return {
    nickname: generateUniqueNickname(),
    avatar: generateAvatar()
  };
};
